import { Component } from 'react';
import { reportError } from '../Api/QuoteApi';

/**
 * Top-level React ErrorBoundary so a render-time crash anywhere in the
 * tree (bad data, stale state, 3rd-party SDK throws inside JSX) doesn't
 * leave the customer staring at a blank page. We:
 *   1. Render a minimal recovery surface (logo + apology + reload button)
 *      so the customer has a path forward instead of a white screen.
 *   2. Fire reportError so the alert email lands in kenji@maximally.io
 *      with the component-stack so we can debug.
 *
 * Wraps each top-level Route element. Per-route boundaries instead of a
 * single app-level one mean a crash in QuotePage doesn't take down the
 * marketing/Step routes too, and vice-versa.
 */
class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, message: '' };
  }

  static getDerivedStateFromError(err) {
    return { hasError: true, message: String(err?.message || err || 'unknown') };
  }

  componentDidCatch(err, info) {
    // Best-effort -- never throw from the error boundary itself.
    try {
      const dealId = (window.location.pathname.match(/\/q\/(\d+)-/) || [])[1]
        || (window.location.pathname.match(/\/agreement\/(\d+)-/) || [])[1]
        || '';
      reportError({
        dealId,
        stage: this.props.stage || 'react_render_error',
        message: `${err?.message || err}\n\nComponent stack: ${(info?.componentStack || '').slice(0, 1500)}`,
      });
    } catch { /* swallow */ }
  }

  render() {
    if (this.state.hasError) {
      return (
        <div style={{
          minHeight: '60vh',
          display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
          padding: '24px', textAlign: 'center', fontFamily: '-apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,sans-serif',
        }}>
          <h2 style={{ margin: '0 0 12px', fontSize: 22, color: '#0a3d5c' }}>Something went wrong on our end.</h2>
          <p style={{ margin: '0 0 18px', maxWidth: 480, color: '#374151' }}>
            We hit an unexpected error loading this page. Our team has been notified. Please reload, or call us at <a href="tel:8884057668" style={{ color: '#27A2D6' }}>(888) 405-7668</a> if it keeps happening.
          </p>
          <button
            onClick={() => window.location.reload()}
            style={{ background: '#27A2D6', color: '#fff', border: 'none', padding: '12px 24px', borderRadius: 8, fontSize: 15, fontWeight: 600, cursor: 'pointer' }}
          >
            Reload page
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

export default ErrorBoundary;
